import { Container, Divider, lighten, makeStyles, Typography } from '@material-ui/core';
import { StaticImage } from 'gatsby-plugin-image';
import React from 'react';
import PageLayout from '../../components/page-layout';
import Section from '../../components/section';
import Seo from '../../components/seo';
import FeatureBlock from './_feature-block';

const useStyles = makeStyles(theme => ({
  featureHeading: {
    marginBottom: theme.spacing(1),
  },
  inlineCode: {
    backgroundColor: `${lighten(theme.palette.secondary.light, 0.1)} !important`,
  },
}));

const ContinuousDeliveryPage: React.FC = () => {
  const classes = useStyles();

  const title = 'Continuous application delivery';
  const description = 'Fast, safe, and repeatable cloud deployments';

  return (
    <PageLayout>
      <Seo title={title} description={description} />

      <Section variant="hero-green">
        <Typography variant="h1" align="center">
          {title}
        </Typography>
        <Typography variant="subtitle1" align="center">
          {description}
        </Typography>
      </Section>

      <FeatureBlock
        image={
          <StaticImage
            src="../../images/icons/lg/intelligent-pipelines.svg"
            alt="Intelligent pipelines"
            loading="eager"
          />
        }
      >
        <Typography variant="h3" className={classes.featureHeading}>
          Intelligent deployment pipelines
        </Typography>
        <Typography variant="body1" paragraph>
          Developers may know how to create their applications, but deploying them is another story.
          Scripting up blue/green pipelines and injecting application secrets are often foreign and
          error-prone tasks.
        </Typography>
        <Typography variant="body1" paragraph>
          Architect automatically detects changes between incoming artifacts/configuration and the
          current environment state to dynamically generate deployment pipelines without any
          additional input from developers.
        </Typography>
      </FeatureBlock>

      <Container>
        <Divider />
      </Container>

      <FeatureBlock
        flipped
        image={
          <StaticImage
            src="../../images/icons/lg/any-cloud-provider.svg"
            alt="Any cloud provider"
          />
        }
      >
        <Typography variant="h3" className={classes.featureHeading}>
          Any cloud provider
        </Typography>
        <Typography variant="body1" paragraph>
          Cloud providers and their service offerings are constantly changing and expanding. Asking
          developers to keep up with features from specific vendors only creates vendor lock-in and
          distracts them from developing product features.
        </Typography>
        <Typography variant="body1" paragraph>
          Instead of asking everyone to learn how to deploy to specific providers, Architect
          automatically deploys apps and services to any provider without any configuration changes.
          Your developers stay focused on features, and you'll always be able to swap out providers.
        </Typography>
      </FeatureBlock>

      <Container>
        <Divider />
      </Container>

      <FeatureBlock
        image={
          <StaticImage src="../../images/icons/lg/service-mesh.svg" alt="On-demand environments" />
        }
      >
        <Typography variant="h3" className={classes.featureHeading}>
          Dependency injection
        </Typography>
        <Typography variant="body1" paragraph>
          Applications are more than their containers. They depend on the ability to connect to
          external dependencies like databases, messaging queues, peer APIs, and more.
        </Typography>
        <Typography variant="body1" paragraph>
          Architect's built-in dependency resolver not only ensures that these external dependencies
          are provisioned, but goes even further to inject the locations of these dependencies into
          consuming applications. This injection means applications need less configuration in order
          to be deployed and minimizes any potential human error.
        </Typography>
      </FeatureBlock>

      <Container>
        <Divider />
      </Container>

      <FeatureBlock
        flipped
        image={
          <StaticImage
            src="../../images/icons/lg/push-button-rollbacks.svg"
            alt="Push-button rollbacks"
          />
        }
      >
        <Typography variant="h3" className={classes.featureHeading}>
          Push-button rollbacks
        </Typography>
        <Typography variant="body1" paragraph>
          Deployment speed is critical to the feature releases and customer experience, but
          developers can still make mistakes and accidentally release buggy or regressive code into
          production.
        </Typography>
        <Typography variant="body1" paragraph>
          Architect keeps track of every change made to your environment, and uses the same
          automated pipeline generation used for deployments to safely roll back changes no matter
          how far back you need to go.
        </Typography>
      </FeatureBlock>
    </PageLayout>
  );
};

export default ContinuousDeliveryPage;
